// ============================================================
// useEnregistrement — le devis s'enregistre tout seul, et ne se perd
// plus en changeant d'écran. 6 octobre 2026.
// ------------------------------------------------------------
// Philippe : « Je travaille sur le devis 062, je vais voir un prix sur
// un autre devis, je reviens à mon 062 et j'ai tout perdu. »
//
// Trois chemins perdaient le travail, et seul le premier était gardé :
//
//  1. fermer l'onglet ou recharger — `beforeunload`, déjà en place ;
//  2. naviguer DANS l'application (menu, autre devis) — rien ne gardait
//     ce chemin : `<BrowserRouter>` n'offre pas `useBlocker` ;
//  3. un RECHARGEMENT du devis par l'écran lui-même : la fin d'une
//     lecture de PDF, un changement de statut. `useResource` repasse en
//     chargement, l'éditeur est démonté, la saisie disparaît. Trouvé en
//     lisant le code le 6 octobre ; l'analyse ne le citait pas.
//
// Le remède est le même pour les trois : ENREGISTRER AVANT. Et pour ne
// pas dépendre de ces portes-là seulement, le devis s'enregistre aussi
// 2,5 s après la dernière modification.
//
// ── UNE SEULE REQUÊTE EN VOL ──
//
// Deux PUT simultanés du même devis s'écraseraient dans un ordre que
// personne ne choisit. Une modification pendant l'envoi est donc mise en
// attente, et renvoyée quand la réponse revient. Cette réponse ne doit
// pas effacer ce qu'on a tapé entre-temps : voir `enregistrement.js`.
// ============================================================

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router';
import { api } from '../../lib/api.js';

/** Le délai après la dernière frappe. Assez pour ne pas envoyer à chaque lettre. */
export const DELAI_AUTO_MS = 2500;

/**
 * @param {object} editor  `useDevisEditor`
 * @param {object} o
 * @param {boolean} o.locked  devis envoyé ou accepté : rien ne part tout seul
 * @param {object} o.toast
 */
export function useEnregistrement(editor, { locked, toast }) {
  const [statut, setStatut] = useState({ etat: 'repos', a: null, erreur: null });
  const [urgent, setUrgent] = useState(0);
  const enVol = useRef(null);
  const aRelancer = useRef(false);
  // Après une suppression : on quitte sans rien enregistrer.
  const libre = useRef(false);
  const ref = useRef({ editor, locked });
  useLayoutEffect(() => {
    ref.current = { editor, locked };
  });

  /**
   * Enregistre, et ne rend la main que quand PLUS RIEN n'est à envoyer.
   * Appelé pendant un envoi, il rejoint cet envoi et le prolonge.
   */
  const enregistrer = useCallback(async ({ silencieux = true } = {}) => {
    if (enVol.current) {
      aRelancer.current = true;
      return enVol.current;
    }
    const tour = (async () => {
      setStatut((s) => ({ ...s, etat: 'envoi', erreur: null }));
      let res;
      try {
        let propre;
        do {
          aRelancer.current = false;
          const ed = ref.current.editor;
          const envoye = ed.instantane();
          // eslint-disable-next-line no-await-in-loop
          res = await api.put(`/devis/${ed.devis.id}`, envoye.payload);
          propre = ref.current.editor.appliquerReponse(envoye, res);
        } while (aRelancer.current || !propre);
        setStatut({ etat: 'ok', a: new Date(), erreur: null });
      } catch (err) {
        setStatut({ etat: 'echec', a: null, erreur: err });
        if (!silencieux) toast.error(err.message);
        throw err;
      } finally {
        enVol.current = null;
      }
      if (!silencieux) {
        if (res.complete) {
          toast.success(`Devis ${res.devis.reference} enregistré.`);
        } else {
          // Enregistré, mais il ne peut pas partir tant qu'une pièce est incomplète.
          toast.warn(
            `Enregistré. ${res.warnings.filter((w) => w.level === 'error').length} valeur(s) manquante(s) : le devis ne peut pas encore être envoyé.`,
          );
        }
      }
      return res;
    })();
    enVol.current = tour;
    return tour;
  }, [toast]);

  /** Tout ce qui est en attente, puis rien. Ne jette pas : rend `false` en échec. */
  const vider = useCallback(async () => {
    const { editor: ed, locked: verrou } = ref.current;
    if (verrou || libre.current) return true;
    try {
      if (enVol.current || ed.etat.current.dirty) await enregistrer();
      return true;
    } catch {
      return false;
    }
  }, [enregistrer]);

  // ── 2,5 s après la dernière modification ──
  useEffect(() => {
    if (!editor.dirty || locked || libre.current) return undefined;
    const t = setTimeout(() => {
      setUrgent(0);
      enregistrer().catch(() => {});
    }, urgent ? 0 : DELAI_AUTO_MS);
    return () => clearTimeout(t);
  }, [editor.revision, editor.dirty, locked, urgent, enregistrer]);

  /**
   * Tout de suite, sans attendre les 2,5 s — après une estimation ou une
   * révision de gamme : une minute et demie de calcul, et son coût, ne
   * restent pas dans la seule mémoire de l'écran. Passe par un rendu,
   * pour que l'envoi parte avec la gamme qui vient d'arriver.
   */
  const bientot = useCallback(() => setUrgent((u) => u + 1), []);

  // ── Fermer l'onglet ou recharger : le navigateur demande ──
  useEffect(() => {
    if (!editor.dirty || locked) return undefined;
    const avant = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avant);
    return () => window.removeEventListener('beforeunload', avant);
  }, [editor.dirty, locked]);

  // ── Naviguer dans l'application : on enregistre, PUIS on part ──
  //
  // Pas de boîte de dialogue : le deviseur n'a rien à décider, son travail
  // part avec lui. Si l'enregistrement échoue, on RESTE — partir
  // reviendrait à perdre en silence ce qu'on n'a pas pu sauver.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (libre.current || ref.current.locked) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return Boolean(enVol.current) || ref.current.editor.etat.current.dirty;
  });
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    vider().then((ok) => {
      if (ok) {
        blocker.proceed();
      } else {
        blocker.reset();
        toast.error('Enregistrement impossible : vous êtes resté sur le devis pour ne rien perdre.');
      }
    });
  }, [blocker, vider, toast]);

  /** Le devis n'existe plus : on part sans rien enregistrer. */
  const liberer = useCallback(() => { libre.current = true; }, []);

  return { statut, enregistrer, vider, bientot, liberer };
}
